import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { tokens } from '@/design';
import { LayoutCanvas } from './LayoutCanvas';
import { LayoutInsertBar } from './LayoutInsertBar';
import {
  LayoutGraphicEditToolbar,
  type GraphicBooleanAction,
  type GraphicOrderAction,
} from './LayoutGraphicEditToolbar';
import { PicturePropertiesPanel } from './PicturePropertiesPanel';
import { GraphicsPropertiesPanel } from './GraphicsPropertiesPanel';
import { resizePictureKeepingAspect } from './layoutPicture';
import { LegendPropertiesPanel } from './LegendPropertiesPanel';
import { NorthArrowPropertiesPanel } from './NorthArrowPropertiesPanel';
import { ScaleBarPropertiesPanel } from './ScaleBarPropertiesPanel';
import { TableFramePropertiesPanel } from './TableFramePropertiesPanel';
import { LayoutWorkspaceBar } from './LayoutWorkspaceBar';
import { loadVectorAttributeFeatures } from '../vector/loadVectorAttributeFeatures';
import {
  attributeColumnsFromFeatures,
  sortAttributeRowsByObjectId,
} from '../vector/vectorAttributeRows';
import { mergeVectorSessionFeatures } from '../vector/vectorEditSession';
import { isVectorToolCardLayer } from '../vector/vectorStyleModel';
import type { ResizeHandleId } from './elementGeometry';
import { resizeRect } from './elementGeometry';
import {
  addLegend,
  addLayoutGraphic,
  addLayoutPicture,
  addNorthArrow,
  addScaleBar,
  addTableFrame,
  createDefaultLayout,
  duplicateElement,
  flipLayoutGraphic,
  moveElement,
  northArrowSizePt,
  pageFromPreset,
  removeElement,
  reorderElement,
  rotateLayoutGraphic,
  setCustomPageSize,
  setNorthArrowSizePt,
  setPagePreset,
  syncTableFrameFields,
  updateElementRect,
  updateLegendProperties,
  updateLayoutGraphic,
  updateLayoutPicture,
  updateMapFrameCamera,
  updateNorthArrowProperties,
  updateNorthArrowSymbol,
  updateScaleBarProperties,
  updateTableFrameProperties,
} from './layoutDocument';
import { normalizedPaths, reverseLayoutGraphicGeometry } from './layoutGraphicGeometry';
import { combinePagePolygons } from './layoutGraphicOperations';
import { legendItemsForLayers } from './legendLayerItems';
import { exportLayoutPng, printLayoutPage } from './exportLayoutPage';
import type { Layer } from '@geosoft/viewer-sdk';
import type {
  LayoutDocument,
  LayoutFrameEngine,
  LayoutGraphicType,
  LayoutGraphicElement,
  LayoutGraphicGeometry,
  LayoutPictureElement,
  LayoutPictureSource,
  LayoutWorkspaceView,
  LegendElement,
  LegendLayerItem,
  MapFrameCamera,
  MapFrameElement,
  NorthArrowElement,
  PagePresetId,
  PageUnits,
  ScaleBarElement,
  TableFrameRuntimeData,
  TableFrameElement,
} from './layoutTypes';

export interface MapLayoutWorkspaceProps {
  readonly view: LayoutWorkspaceView;
  readonly onViewChange: (view: LayoutWorkspaceView) => void;
  readonly mapPane: ReactNode;
  /** Layers the map tab renders; the page's map frame mounts the same list. */
  readonly frameLayers: readonly Layer[];
  readonly frameEngine: LayoutFrameEngine;
  readonly seedCamera: MapFrameCamera;
  readonly legendLayers: readonly LegendLayerItem[];
  readonly visibleLayerIds: ReadonlySet<string>;
}

function normalizedClosed(geometry: LayoutGraphicGeometry): boolean {
  const paths = normalizedPaths(geometry);
  return paths.length > 0 && paths.every((path) => path.closed && path.points.length >= 3);
}

export function MapLayoutWorkspace({
  view,
  onViewChange,
  mapPane,
  frameLayers,
  frameEngine,
  seedCamera,
  legendLayers,
  visibleLayerIds,
}: MapLayoutWorkspaceProps) {
  const [document, setDocument] = useState<LayoutDocument>(() =>
    createDefaultLayout(seedCamera),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [editGraphicId, setEditGraphicId] = useState<string | null>(null);
  const [pageZoomPercent, setPageZoomPercent] = useState(65);
  const [drawingTool, setDrawingTool] = useState<LayoutGraphicType | null>(null);
  const [pendingPicture, setPendingPicture] = useState<LayoutPictureSource | null>(null);
  const [keepGraphicToolActive, setKeepGraphicToolActive] = useState(false);
  /** The page (and its own engine) is built on first visit, then kept alive. */
  const [layoutMounted, setLayoutMounted] = useState(view !== 'map');
  const mapCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const layoutPaneRef = useRef<HTMLDivElement | null>(null);
  const documentRef = useRef(document);
  const undoRef = useRef<LayoutDocument[]>([]);
  const redoRef = useRef<LayoutDocument[]>([]);
  const gestureSnapshotRef = useRef<LayoutDocument | null>(null);
  const vertexSnapshotRef = useRef<LayoutDocument | null>(null);
  const seededRef = useRef(false);
  const tableLoadsRef = useRef(new Set<string>());
  const [tableDataByLayer, setTableDataByLayer] = useState<
    Readonly<Record<string, TableFrameRuntimeData>>
  >({});
  const tableSourceKey = document.elements
    .flatMap((element) =>
      element.type === 'tableFrame' && element.sourceLayerId
        ? [element.sourceLayerId]
        : [],
    )
    .sort()
    .join('|');

  useEffect(() => {
    documentRef.current = document;
  }, [document]);

  const replaceDocument = useCallback((next: LayoutDocument) => {
    documentRef.current = next;
    setDocument(next);
  }, []);

  const commitGraphicMutation = useCallback((
    updater: (current: LayoutDocument) => LayoutDocument,
  ) => {
    const current = documentRef.current;
    const next = updater(current);
    if (next === current) return;
    undoRef.current.push(current);
    redoRef.current = [];
    replaceDocument(next);
  }, [replaceDocument]);

  const selectElement = useCallback((id: string | null, additive = false) => {
    if (!id) {
      if (!additive) {
        setSelectedIds(new Set());
        setSelectedId(null);
        setEditGraphicId(null);
      }
      return;
    }
    setSelectedIds((current) => {
      if (!additive) {
        setSelectedId(id);
        return new Set([id]);
      }
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setSelectedId(next.has(id) ? id : Array.from(next).at(-1) ?? null);
      return next;
    });
    setEditGraphicId(null);
  }, []);

  const matchMapView = useCallback((next: MapFrameCamera) => {
    setDocument((prev) => {
      const frame = prev.elements.find((el) => el.type === 'mapFrame');
      if (!frame) return prev;
      return updateMapFrameCamera(prev, frame.id, next);
    });
  }, []);

  // The page opens on whatever the map tab is showing. Seeding waits for a
  // real camera because the store starts at a placeholder world view.
  useEffect(() => {
    if (seededRef.current) return;
    const hasRealView =
      seedCamera.zoom > 2 ||
      seedCamera.center[0] !== 0 ||
      seedCamera.center[1] !== 0;
    if (!hasRealView) return;
    seededRef.current = true;
    matchMapView(seedCamera);
  }, [matchMapView, seedCamera]);

  // Panes only change size on a view switch, so give both engines a beat to
  // re-measure once the new styles have painted.
  useEffect(() => {
    if (view !== 'map') setLayoutMounted(true);
    const frame = requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
    return () => cancelAnimationFrame(frame);
  }, [view]);

  useEffect(() => {
    const sourceIds = tableSourceKey.split('|').filter(Boolean);
    sourceIds.forEach((layerId) => {
      if (tableLoadsRef.current.has(layerId)) return;
      const layer = frameLayers.find((candidate) => candidate.id === layerId);
      if (!layer) return;
      tableLoadsRef.current.add(layerId);
      setTableDataByLayer((current) => ({
        ...current,
        [layerId]: {
          layerId,
          fields: [],
          rows: [],
          loading: true,
          error: null,
        },
      }));
      void loadVectorAttributeFeatures(layer)
        .then((features) => {
          const normalized = sortAttributeRowsByObjectId(
            mergeVectorSessionFeatures(layerId, features),
          );
          const rows = normalized.map((feature) => feature.properties ?? {});
          const fields = attributeColumnsFromFeatures(normalized);
          setTableDataByLayer((current) => ({
            ...current,
            [layerId]: {
              layerId,
              fields,
              rows,
              loading: false,
              error: null,
            },
          }));
          setDocument((current) => {
            let next = current;
            current.elements.forEach((element) => {
              if (
                element.type === 'tableFrame'
                && element.sourceLayerId === layerId
              ) {
                next = syncTableFrameFields(next, element.id, fields);
              }
            });
            return next;
          });
        })
        .catch((error: unknown) => {
          setTableDataByLayer((current) => ({
            ...current,
            [layerId]: {
              layerId,
              fields: [],
              rows: [],
              loading: false,
              error:
                error instanceof Error
                  ? error.message
                  : 'Unable to load table records',
            },
          }));
        });
    });
  }, [frameLayers, tableSourceKey]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (view === 'map') return;
      if (!layoutPaneRef.current?.contains(globalThis.document.activeElement)) return;
      const target = event.target as HTMLElement | null;
      if (target && (
        target.tagName === 'INPUT'
        || target.tagName === 'TEXTAREA'
        || target.tagName === 'SELECT'
        || target.isContentEditable
      )) {
        return;
      }
      const command = event.ctrlKey || event.metaKey;
      if (command && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        const source = event.shiftKey ? redoRef : undoRef;
        const destination = event.shiftKey ? undoRef : redoRef;
        const next = source.current.pop();
        if (!next) return;
        destination.current.push(documentRef.current);
        replaceDocument(next);
        return;
      }
      if (command && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        const next = redoRef.current.pop();
        if (!next) return;
        undoRef.current.push(documentRef.current);
        replaceDocument(next);
        return;
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (!selectedIds.size || editGraphicId) return;
        event.preventDefault();
        commitGraphicMutation((current) => {
          let next = current;
          selectedIds.forEach((id) => {
            const element = current.elements.find((candidate) => candidate.id === id);
            if (element && (element.type === 'graphic' || element.type === 'picture')) {
              next = removeElement(next, id);
            }
          });
          return next;
        });
        setSelectedIds(new Set());
        setSelectedId(null);
        return;
      }
      const nudge: Record<string, readonly [number, number]> = {
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
        ArrowUp: [0, -1],
        ArrowDown: [0, 1],
      };
      const delta = nudge[event.key];
      if (!delta || !selectedIds.size || editGraphicId) return;
      event.preventDefault();
      const factor = event.shiftKey ? 10 : 1;
      commitGraphicMutation((current) => {
        let next = current;
        selectedIds.forEach((id) => {
          const element = current.elements.find((candidate) => candidate.id === id);
          if (element && (element.type === 'graphic' || element.type === 'picture') && !element.locked) {
            next = moveElement(next, id, delta[0] * factor, delta[1] * factor);
          }
        });
        return next;
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    commitGraphicMutation,
    editGraphicId,
    replaceDocument,
    selectedIds,
    view,
  ]);

  const onMove = useCallback((id: string, dx: number, dy: number) => {
    const ids = selectedIds.has(id) ? selectedIds : new Set([id]);
    setDocument((prev) => {
      let next = prev;
      ids.forEach((selected) => {
        const element = prev.elements.find((candidate) => candidate.id === selected);
        if (!element || element.type === 'mapFrame') return;
        if ('locked' in element && element.locked) return;
        next = moveElement(next, selected, dx, dy);
      });
      documentRef.current = next;
      return next;
    });
  }, [selectedIds]);

  const onResize = useCallback(
    (id: string, handle: ResizeHandleId, pointerMm: { x: number; y: number }) => {
      setDocument((prev) => {
        const current = prev.elements.find((el) => el.id === id);
        if (!current) return prev;
        if (current.type === 'picture' && current.preserveAspectRatio && !current.locked) {
          const next = updateElementRect(
            prev,
            id,
            resizePictureKeepingAspect(
              current.rect,
              resizeRect(current.rect, handle, pointerMm),
              current.source.naturalWidthPx,
              current.source.naturalHeightPx,
            ),
          );
          documentRef.current = next;
          return next;
        }
        const graphics = selectedIds.has(id)
          ? prev.elements.filter((element): element is LayoutGraphicElement =>
              element.type === 'graphic' && selectedIds.has(element.id) && !element.locked)
          : [];
        if (graphics.length > 1) {
          const left = Math.min(...graphics.map((graphic) => graphic.rect.x));
          const top = Math.min(...graphics.map((graphic) => graphic.rect.y));
          const right = Math.max(...graphics.map((graphic) => graphic.rect.x + graphic.rect.width));
          const bottom = Math.max(...graphics.map((graphic) => graphic.rect.y + graphic.rect.height));
          const group = { x: left, y: top, width: right - left, height: bottom - top };
          const resized = resizeRect(group, handle, pointerMm);
          const scaleX = resized.width / group.width;
          const scaleY = resized.height / group.height;
          let next = prev;
          graphics.forEach((graphic) => {
            next = updateElementRect(next, graphic.id, {
              x: resized.x + (graphic.rect.x - group.x) * scaleX,
              y: resized.y + (graphic.rect.y - group.y) * scaleY,
              width: graphic.rect.width * scaleX,
              height: graphic.rect.height * scaleY,
            });
          });
          documentRef.current = next;
          return next;
        }
        const next = updateElementRect(prev, id, resizeRect(current.rect, handle, pointerMm));
        documentRef.current = next;
        return next;
      });
    },
    [selectedIds],
  );

  const onRotate = useCallback((id: string, degrees: number) => {
    const ids = selectedIds.has(id) ? selectedIds : new Set([id]);
    setDocument((prev) => {
      let next = prev;
      ids.forEach((selected) => {
        next = rotateLayoutGraphic(next, selected, degrees);
      });
      documentRef.current = next;
      return next;
    });
  }, [selectedIds]);

  const beginGraphicGesture = useCallback(() => {
    gestureSnapshotRef.current = documentRef.current;
  }, []);

  const endGraphicGesture = useCallback(() => {
    const snapshot = gestureSnapshotRef.current;
    gestureSnapshotRef.current = null;
    if (!snapshot || snapshot === documentRef.current) return;
    undoRef.current.push(snapshot);
    redoRef.current = [];
  }, []);

  const fitPageZoom = useCallback((widthMm: number, heightMm: number) => {
    const longestSide = Math.max(widthMm, heightMm);
    // A4 at 65% is the established comfortable size; preserve that visual
    // footprint for A0, ANSI E, and custom sheets.
    return Math.max(5, Math.min(100, Math.round((65 * 297) / longestSide)));
  }, []);

  const onPaperPreset = useCallback(
    (presetId: Exclude<PagePresetId, 'custom'>) => {
      const page = pageFromPreset(presetId);
      setDocument((previous) => setPagePreset(previous, presetId));
      setPageZoomPercent(fitPageZoom(page.widthMm, page.heightMm));
    },
    [fitPageZoom],
  );

  const onCustomPageSize = useCallback(
    (widthMm: number, heightMm: number, units: PageUnits) => {
      setDocument((previous) =>
        setCustomPageSize(previous, widthMm, heightMm, units),
      );
      setPageZoomPercent(fitPageZoom(widthMm, heightMm));
    },
    [fitPageZoom],
  );

  const hasSingleSelection = selectedIds.size === 1;
  const selectedGraphics = Array.from(selectedIds)
    .map((id) => document.elements.find((element) => element.id === id))
    .filter((element): element is LayoutGraphicElement => element?.type === 'graphic');
  const selectedGraphic = hasSingleSelection ? selectedGraphics[0] : undefined;
  const selectedPicture = hasSingleSelection ? document.elements.find(
    (element): element is LayoutPictureElement =>
      element.type === 'picture' && element.id === selectedId,
  ) : undefined;
  const selectedLegend = hasSingleSelection ? document.elements.find(
    (element): element is LegendElement =>
      element.type === 'legend' && element.id === selectedId,
  ) : undefined;
  const selectedLegendItems = selectedLegend
    ? legendItemsForLayers(legendLayers, visibleLayerIds)
    : [];
  const selectedNorthArrow = hasSingleSelection ? document.elements.find(
    (element): element is NorthArrowElement =>
      element.type === 'northArrow' && element.id === selectedId,
  ) : undefined;
  const selectedScaleBar = hasSingleSelection ? document.elements.find(
    (element): element is ScaleBarElement =>
      element.type === 'scaleBar' && element.id === selectedId,
  ) : undefined;
  const selectedTableFrame = hasSingleSelection ? document.elements.find(
    (element): element is TableFrameElement =>
      element.type === 'tableFrame' && element.id === selectedId,
  ) : undefined;
  const mapFrames = document.elements.filter(
    (element): element is MapFrameElement => element.type === 'mapFrame',
  );

  const applyToSelectedGraphics = useCallback((
    update: (current: LayoutDocument, id: string) => LayoutDocument,
  ) => {
    commitGraphicMutation((current) => {
      let next = current;
      selectedIds.forEach((id) => {
        if (current.elements.some((element) => element.id === id && element.type === 'graphic')) {
          next = update(next, id);
        }
      });
      return next;
    });
  }, [commitGraphicMutation, selectedIds]);

  const combineSelected = useCallback((action: GraphicBooleanAction) => {
    const graphics = Array.from(selectedIds)
      .map((id) => documentRef.current.elements.find((element) => element.id === id))
      .filter((element): element is LayoutGraphicElement =>
        element?.type === 'graphic'
        && !element.locked
        && normalizedClosed(element.geometry));
    const result = combinePagePolygons(action, graphics);
    if (!result || !graphics[0]) return;
    const keeper = graphics[0];
    commitGraphicMutation((current) => {
      let next = updateElementRect(current, keeper.id, result.rect);
      next = updateLayoutGraphic(next, keeper.id, { geometry: result.geometry });
      graphics.slice(1).forEach((graphic) => {
        next = removeElement(next, graphic.id);
      });
      return next;
    });
    setSelectedIds(new Set([keeper.id]));
    setSelectedId(keeper.id);
  }, [commitGraphicMutation, selectedIds]);

  const orderSelected = useCallback((action: GraphicOrderAction) => {
    applyToSelectedGraphics((current, id) => reorderElement(current, id, action));
  }, [applyToSelectedGraphics]);

  const layoutPane = (
    <div
      ref={layoutPaneRef}
      data-testid="layout-workspace-pane"
      tabIndex={-1}
      onPointerDownCapture={() => {
        if (!layoutPaneRef.current?.contains(globalThis.document.activeElement)) {
          layoutPaneRef.current?.focus({ preventScroll: true });
        }
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        background: tokens.color.surface[0],
        position: 'relative',
      }}
    >
      <LayoutInsertBar
        paperPreset={document.page.presetId}
        paperWidthMm={document.page.widthMm}
        paperHeightMm={document.page.heightMm}
        paperUnits={document.page.units}
        onPaperPreset={onPaperPreset}
        onCustomPageSize={onCustomPageSize}
        pageZoomPercent={pageZoomPercent}
        onPageZoomPercent={setPageZoomPercent}
        onInsertNorthArrow={(styleId) => setDocument((prev) => addNorthArrow(prev, styleId))}
        onInsertScaleBar={(styleId, units) =>
          setDocument((prev) => addScaleBar(prev, styleId, units))
        }
        onInsertLegend={(styleId) =>
          setDocument((prev) => addLegend(prev, styleId))
        }
        onInsertTableFrame={(styleId) =>
          setDocument((prev) =>
            addTableFrame(
              prev,
              styleId,
              frameLayers.find(isVectorToolCardLayer)?.id ?? null,
            ),
          )
        }
        activeGraphicTool={drawingTool}
        keepGraphicToolActive={keepGraphicToolActive}
        onSelectGraphicTool={(tool) => {
          setSelectedIds(new Set());
          setSelectedId(null);
          setEditGraphicId(null);
          setPendingPicture(null);
          setDrawingTool(tool);
        }}
        onKeepGraphicToolActive={setKeepGraphicToolActive}
        onPictureSource={(source) => {
          setSelectedIds(new Set());
          setSelectedId(null);
          setEditGraphicId(null);
          setDrawingTool(null);
          setPendingPicture(source);
        }}
        onMatchMapView={() => matchMapView(seedCamera)}
        onExportPng={() =>
          exportLayoutPng(document, {
            mapCanvas: mapCanvasRef.current,
            legendLayers,
            visibleLayerIds,
            tableDataByLayer,
          })
        }
        onPrint={() =>
          printLayoutPage(document, {
            mapCanvas: mapCanvasRef.current,
            legendLayers,
            visibleLayerIds,
            tableDataByLayer,
          })
        }
      />
      <LayoutCanvas
        document={document}
        selectedId={selectedId}
        selectedIds={selectedIds}
        pageZoom={pageZoomPercent / 100}
        frameLayers={frameLayers}
        frameEngine={frameEngine}
        legendLayers={legendLayers}
        visibleLayerIds={visibleLayerIds}
        tableDataByLayer={tableDataByLayer}
        onSelect={selectElement}
        onMove={onMove}
        onResize={onResize}
        onRotate={onRotate}
        onGestureStart={beginGraphicGesture}
        onGestureEnd={endGraphicGesture}
        editGraphicId={editGraphicId}
        onVertexGeometry={(id, geometry) => {
          setDocument((current) => {
            const next = updateLayoutGraphic(current, id, { geometry });
            documentRef.current = next;
            return next;
          });
        }}
        onFinishVertexEdit={() => {
          const snapshot = vertexSnapshotRef.current;
          vertexSnapshotRef.current = null;
          if (snapshot && snapshot !== documentRef.current) {
            undoRef.current.push(snapshot);
            redoRef.current = [];
          }
          setEditGraphicId(null);
        }}
        onCancelVertexEdit={() => {
          if (vertexSnapshotRef.current) replaceDocument(vertexSnapshotRef.current);
          vertexSnapshotRef.current = null;
          setEditGraphicId(null);
        }}
        onTextCommit={(id, text) =>
          commitGraphicMutation((current) => updateLayoutGraphic(current, id, { text: { text } }))
        }
        onMapFrameCamera={(frameId, camera) =>
          setDocument((prev) => updateMapFrameCamera(prev, frameId, camera))
        }
        onMapCanvasReady={(canvas) => {
          mapCanvasRef.current = canvas;
        }}
        drawingTool={drawingTool}
        onDrawingComplete={(graphic) => {
          commitGraphicMutation((previous) => {
            const next = addLayoutGraphic(previous, graphic.type, {
              rect: graphic.rect,
              geometry: graphic.geometry,
            });
            const id = next.elements.at(-1)?.id ?? null;
            setSelectedId(id);
            setSelectedIds(id ? new Set([id]) : new Set());
            return next;
          });
          if (!keepGraphicToolActive) setDrawingTool(null);
        }}
        onDrawingCancel={() => setDrawingTool(null)}
        pendingPicture={pendingPicture}
        onPicturePlaced={(placement) => {
          if (!pendingPicture) return;
          commitGraphicMutation((previous) => {
            const next = addLayoutPicture(previous, pendingPicture, {
              rect: placement.rect,
              preserveAspectRatio: placement.preserveAspectRatio,
            });
            const id = next.elements.at(-1)?.id ?? null;
            setSelectedId(id);
            setSelectedIds(id ? new Set([id]) : new Set());
            return next;
          });
          setPendingPicture(null);
        }}
        onPicturePlaceCancel={() => setPendingPicture(null)}
      />
      {selectedGraphics.length ? (
        <LayoutGraphicEditToolbar
          count={selectedGraphics.length}
          canEditVertices={selectedGraphics.length === 1}
          editingVertices={editGraphicId !== null}
          canCombine={
            selectedGraphics.length > 1
            && selectedGraphics.every((graphic) => normalizedClosed(graphic.geometry))
          }
          onEditVertices={() => {
            const graphic = selectedGraphics[0];
            if (!graphic) return;
            if (editGraphicId === graphic.id) {
              const snapshot = vertexSnapshotRef.current;
              if (snapshot && snapshot !== documentRef.current) {
                undoRef.current.push(snapshot);
                redoRef.current = [];
              }
              setEditGraphicId(null);
              vertexSnapshotRef.current = null;
            } else {
              vertexSnapshotRef.current = documentRef.current;
              setEditGraphicId(graphic.id);
            }
          }}
          onReverse={() =>
            applyToSelectedGraphics((current, id) => {
              const graphic = current.elements.find((element) =>
                element.type === 'graphic' && element.id === id);
              return graphic?.type === 'graphic'
                ? updateLayoutGraphic(current, id, {
                    geometry: reverseLayoutGraphicGeometry(graphic.geometry),
                  })
                : current;
            })
          }
          onRotate={(degrees) =>
            applyToSelectedGraphics((current, id) => rotateLayoutGraphic(current, id, degrees))
          }
          onFlip={(axis) =>
            applyToSelectedGraphics((current, id) => flipLayoutGraphic(current, id, axis))
          }
          onDuplicate={() => {
            const created: string[] = [];
            commitGraphicMutation((current) => {
              let next = current;
              selectedGraphics.forEach((graphic, index) => {
                const before = new Set(next.elements.map((element) => element.id));
                next = duplicateElement(next, graphic.id, 5 + index);
                const clone = next.elements.find((element) => !before.has(element.id));
                if (clone) created.push(clone.id);
              });
              return next;
            });
            if (created.length) {
              setSelectedIds(new Set(created));
              setSelectedId(created.at(-1) ?? null);
            }
          }}
          onOrder={orderSelected}
          onCombine={combineSelected}
        />
      ) : null}
      {selectedGraphic ? (
        <GraphicsPropertiesPanel
          graphic={selectedGraphic}
          pageWidthMm={document.page.widthMm}
          pageHeightMm={document.page.heightMm}
          pageUnits={document.page.units}
          onChange={(patch) =>
            commitGraphicMutation((current) =>
              updateLayoutGraphic(current, selectedGraphic.id, patch))
          }
          onRectChange={(rect) =>
            commitGraphicMutation((current) =>
              updateElementRect(current, selectedGraphic.id, rect))
          }
          onDelete={() => {
            commitGraphicMutation((current) => removeElement(current, selectedGraphic.id));
            selectElement(null);
          }}
          onClose={() => selectElement(null)}
        />
      ) : null}
      {selectedPicture ? (
        <PicturePropertiesPanel
          picture={selectedPicture}
          pageWidthMm={document.page.widthMm}
          pageHeightMm={document.page.heightMm}
          pageUnits={document.page.units}
          onChange={(patch) =>
            commitGraphicMutation((current) =>
              updateLayoutPicture(current, selectedPicture.id, patch))
          }
          onRectChange={(rect) =>
            commitGraphicMutation((current) =>
              updateElementRect(current, selectedPicture.id, rect))
          }
          onDelete={() => {
            commitGraphicMutation((current) => removeElement(current, selectedPicture.id));
            selectElement(null);
          }}
          onClose={() => selectElement(null)}
        />
      ) : null}
      <div
        role="status"
        aria-live="polite"
        style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}
      >
        {pendingPicture
          ? 'Placing picture. Click for original size or drag a box. Press Escape to cancel.'
          : drawingTool
          ? `Drawing ${drawingTool}. Press Escape to cancel.`
          : selectedId ? 'Select tool active.' : 'Select tool active. No element selected.'}
      </div>
      {selectedLegend ? (
        <LegendPropertiesPanel
          legend={selectedLegend}
          items={selectedLegendItems}
          frames={mapFrames}
          onChange={(patch) =>
            setDocument((prev) =>
              updateLegendProperties(prev, selectedLegend.id, patch),
            )
          }
          onDelete={() => {
            setDocument((prev) => removeElement(prev, selectedLegend.id));
            selectElement(null);
          }}
          onClose={() => selectElement(null)}
        />
      ) : null}
      {selectedNorthArrow ? (
        <NorthArrowPropertiesPanel
          arrow={selectedNorthArrow}
          frames={mapFrames}
          sizePt={northArrowSizePt(selectedNorthArrow)}
          onChange={(patch) =>
            setDocument((previous) =>
              updateNorthArrowProperties(previous, selectedNorthArrow.id, patch),
            )
          }
          onSymbolApply={(draft) =>
            setDocument((previous) => {
              const withStyle = updateNorthArrowProperties(
                previous,
                selectedNorthArrow.id,
                { styleId: draft.styleId },
              );
              const withSymbol = updateNorthArrowSymbol(
                withStyle,
                selectedNorthArrow.id,
                draft.symbol,
              );
              return setNorthArrowSizePt(
                withSymbol,
                selectedNorthArrow.id,
                draft.sizePt,
              );
            })
          }
          onDelete={() => {
            setDocument((previous) => removeElement(previous, selectedNorthArrow.id));
            selectElement(null);
          }}
          onClose={() => selectElement(null)}
        />
      ) : null}
      {selectedScaleBar ? (
        <ScaleBarPropertiesPanel
          scaleBar={selectedScaleBar}
          frames={mapFrames}
          onChange={(patch) =>
            setDocument((previous) =>
              updateScaleBarProperties(previous, selectedScaleBar.id, patch),
            )
          }
          onDelete={() => {
            setDocument((previous) => removeElement(previous, selectedScaleBar.id));
            selectElement(null);
          }}
          onClose={() => selectElement(null)}
        />
      ) : null}
      {selectedTableFrame ? (
        <TableFramePropertiesPanel
          tableFrame={selectedTableFrame}
          frames={mapFrames}
          layers={frameLayers}
          data={
            selectedTableFrame.sourceLayerId
              ? tableDataByLayer[selectedTableFrame.sourceLayerId] ?? null
              : null
          }
          onChange={(patch) =>
            setDocument((previous) =>
              updateTableFrameProperties(
                previous,
                selectedTableFrame.id,
                patch,
              ),
            )
          }
          onDelete={() => {
            setDocument((previous) =>
              removeElement(previous, selectedTableFrame.id),
            );
            selectElement(null);
          }}
          onClose={() => selectElement(null)}
        />
      ) : null}
    </div>
  );

  /**
   * Both panes stay in one stable element tree for every view: moving the map
   * host between wrappers would make React drop and rebuild it, which tears
   * down the live engine and leaves the map tab blank on the way back. Only
   * the styles change. The hidden map also keeps its full box so returning to
   * it needs no re-measure.
   */
  const mapPaneStyle: CSSProperties =
    view === 'layout'
      ? {
          position: 'absolute',
          inset: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
          zIndex: 0,
        }
      : {
          position: 'relative',
          flex: view === 'split' ? '1 1 50%' : '1 1 100%',
          minWidth: 0,
          minHeight: 0,
        };

  const layoutPaneStyle: CSSProperties =
    view === 'map'
      ? { display: 'none' }
      : {
          position: 'relative',
          flex: view === 'split' ? '1 1 50%' : '1 1 100%',
          minWidth: 0,
          minHeight: 0,
        };

  return (
    <div
      data-testid="map-layout-workspace"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <LayoutWorkspaceBar view={view} onViewChange={onViewChange} />
      <div
        data-testid={view === 'split' ? 'layout-split-pane' : 'layout-workspace-body'}
        style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', position: 'relative' }}
      >
        <div
          data-testid="map-workspace-pane"
          style={mapPaneStyle}
          aria-hidden={view === 'layout'}
        >
          {mapPane}
        </div>
        {view === 'split' ? (
          <div
            aria-hidden
            style={{
              width: tokens.border.width.thin,
              background: tokens.color.surface[3],
              flex: '0 0 auto',
            }}
          />
        ) : null}
        <div style={layoutPaneStyle}>{layoutMounted ? layoutPane : null}</div>
      </div>
    </div>
  );
}
